import React, { useEffect, useState, useCallback, useMemo } from 'react';
import FitTable from '../components/FitTable';
import { useAuth0 } from '@auth0/auth0-react';

const APIURL = process.env.REACT_APP_API_URL;

// ── Types ───────────────────────────────────────────────────────────────────

interface WhatsAppStatus {
  status: 'disconnected' | 'connecting' | 'qr' | 'connected';
  qrDataUrl: string | null;
  me: { id: string; name: string } | null;
  lastError: string | null;
  connectedAt: string | null;
}

interface WhatsAppGroup {
  id: string;
  name: string;
  participants: number;
}

type Segment =
  | { type: 'text'; value: string }
  | { type: 'token'; name: TokenName; prefix?: string; suffix?: string };

type TokenName =
  | 'venue' | 'facility' | 'course' | 'time'
  | 'num_players' | 'joined_count' | 'open_seats'
  | 'reservation_name' | 'email_alias' | 'joined_players';

interface Schedule {
  schedule_id: number;
  name: string;
  status: 'active' | 'paused';
  frequency: 'daily' | 'weekly';
  days_of_week: string[];
  time_of_day: string;
  timezone: string;
  target_chat_ids: string[];
  target_chat_names: string[];
  date_range_start_offset: number;
  date_range_days: number;
  message_prefix: string | null;
  header_text: string;
  message_suffix: string | null;
  line_template: Segment[] | null;
  reservation_statuses: string[];
  only_with_open_seats: boolean;
  blank_line_between_tee_times: boolean;
  strikethrough_cancelled: boolean;
  last_sent_at: string | null;
}

// ── Defaults ────────────────────────────────────────────────────────────────

const DEFAULT_TEMPLATE: Segment[] = [
  { type: 'token', name: 'venue' },
  { type: 'token', name: 'time', prefix: ' ' },
  { type: 'token', name: 'num_players', prefix: ' - ' },
  { type: 'token', name: 'open_seats', prefix: ' - ' },
  { type: 'token', name: 'reservation_name', prefix: ' - Reserved under ' },
  { type: 'token', name: 'email_alias', prefix: ' + ' },
];

const EMPTY_DRAFT: Partial<Schedule> = {
  name: '',
  status: 'active',
  frequency: 'weekly',
  days_of_week: ['thursday'],
  time_of_day: '09:00',
  timezone: 'America/Toronto',
  target_chat_ids: [],
  target_chat_names: [],
  date_range_start_offset: 0,
  date_range_days: 7,
  message_prefix: '',
  header_text: 'Tee Time Alert:',
  message_suffix: '',
  line_template: DEFAULT_TEMPLATE,
  reservation_statuses: ['active'],
  only_with_open_seats: false,
  blank_line_between_tee_times: false,
  strikethrough_cancelled: false,
};

const TOKEN_META: Record<TokenName, { label: string; sample: string }> = {
  venue:            { label: 'Facility + Course',  sample: 'Glen Abbey - Island Course' },
  facility:         { label: 'Facility only',      sample: 'Glen Abbey' },
  course:           { label: 'Course only',        sample: 'Island Course' },
  time:             { label: 'Tee time (12hr)',    sample: '7:44 AM' },
  num_players:      { label: '# players',          sample: '4' },
  joined_count:     { label: '# joined (RSVPed)',  sample: '2' },
  open_seats:       { label: '# open seats',       sample: '2' },
  reservation_name: { label: 'Name on file',       sample: 'Smith' },
  email_alias:      { label: 'Booking email',      sample: 'twong60@bgagolf.ca' },
  joined_players:   { label: 'Joined players',     sample: 'Terry Wong, Mike Lee' },
};

const DAYS: { value: string; label: string }[] = [
  { value: 'sunday',    label: 'Sun' },
  { value: 'monday',    label: 'Mon' },
  { value: 'tuesday',   label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday',  label: 'Thu' },
  { value: 'friday',    label: 'Fri' },
  { value: 'saturday',  label: 'Sat' },
];

// ── Page ────────────────────────────────────────────────────────────────────

export default function WhatsAppSettings() {
  const { getAccessTokenSilently } = useAuth0();
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [groups, setGroups] = useState<WhatsAppGroup[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [editing, setEditing] = useState<Partial<Schedule> | null>(null);
  const [preview, setPreview] = useState<{ scope: string; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const token = useCallback(() => getAccessTokenSilently(), [getAccessTokenSilently]);

  const fetchStatus = useCallback(async () => {
    const t = await token();
    const res = await fetch(`${APIURL}/whatsapp/status`, { headers: { Authorization: `Bearer ${t}` } });
    setStatus(await res.json());
  }, [token]);

  const fetchGroups = useCallback(async (refresh = false) => {
    const t = await token();
    const res = await fetch(`${APIURL}/whatsapp/chats${refresh ? '?refresh=true' : ''}`, {
      headers: { Authorization: `Bearer ${t}` },
    });
    if (res.ok) setGroups(await res.json());
  }, [token]);

  const fetchSchedules = useCallback(async () => {
    const t = await token();
    const res = await fetch(`${APIURL}/whatsapp/schedules`, { headers: { Authorization: `Bearer ${t}` } });
    setSchedules(await res.json());
  }, [token]);

  useEffect(() => { fetchStatus(); fetchSchedules(); }, [fetchStatus, fetchSchedules]);

  // Poll status while pairing so the QR refreshes and 'connected' shows up.
  useEffect(() => {
    if (!status) return;
    if (status.status === 'connected' || status.status === 'disconnected') return;
    const t = setInterval(fetchStatus, 2000);
    return () => clearInterval(t);
  }, [status, fetchStatus]);

  useEffect(() => { if (status?.status === 'connected') fetchGroups(); }, [status?.status, fetchGroups]);

  async function connect() {
    setBusy(true); setError('');
    try {
      const t = await token();
      await fetch(`${APIURL}/whatsapp/connect`, { method: 'POST', headers: { Authorization: `Bearer ${t}` } });
      await fetchStatus();
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }

  async function disconnect() {
    if (!window.confirm('Log out the BGA Reservations session? You will need to re-scan the QR to reconnect.')) return;
    setBusy(true);
    try {
      const t = await token();
      await fetch(`${APIURL}/whatsapp/disconnect`, { method: 'POST', headers: { Authorization: `Bearer ${t}` } });
      await fetchStatus();
      setGroups([]);
    } finally { setBusy(false); }
  }

  function startNew() { setEditing({ ...EMPTY_DRAFT, line_template: [...DEFAULT_TEMPLATE] }); }
  function startEdit(s: Schedule) {
    setEditing({
      ...s,
      line_template: Array.isArray(s.line_template) && s.line_template.length > 0
        ? s.line_template
        : [...DEFAULT_TEMPLATE],
    });
  }
  function cancelEdit() { setEditing(null); setError(''); }

  async function saveSchedule() {
    if (!editing) return;
    setBusy(true); setError('');
    try {
      // Pull display names for the selected group IDs
      const names = (editing.target_chat_ids || []).map((id) => {
        const g = groups.find((x) => x.id === id);
        return g?.name || '';
      });
      const payload: Partial<Schedule> = { ...editing, target_chat_names: names };

      const t = await token();
      const isUpdate = !!editing.schedule_id;
      const url = isUpdate
        ? `${APIURL}/whatsapp/schedules/${editing.schedule_id}`
        : `${APIURL}/whatsapp/schedules`;
      const res = await fetch(url, {
        method: isUpdate ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Save failed');
      }
      await fetchSchedules();
      setEditing(null);
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }

  async function deleteSchedule(id: number) {
    if (!window.confirm('Delete this schedule?')) return;
    const t = await token();
    await fetch(`${APIURL}/whatsapp/schedules/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${t}` } });
    await fetchSchedules();
  }

  async function previewSaved(id: number) {
    setBusy(true); setError('');
    try {
      const t = await token();
      const res = await fetch(`${APIURL}/whatsapp/schedules/${id}/preview`, {
        method: 'POST', headers: { Authorization: `Bearer ${t}` },
      });
      const body = await res.json();
      setPreview({
        scope: `Schedule #${id}`,
        text: body.empty ? '(no reservations match this schedule\'s filters)' : body.text,
      });
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }

  async function previewDraft() {
    if (!editing) return;
    setBusy(true); setError('');
    try {
      const t = await token();
      const res = await fetch(`${APIURL}/whatsapp/preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(editing),
      });
      const body = await res.json();
      setPreview({
        scope: 'Draft preview',
        text: body.empty ? '(no reservations match the current filters)' : body.text,
      });
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }

  async function sendNow(id: number) {
    if (!window.confirm('Send this announcement now to all target groups?')) return;
    setBusy(true); setError('');
    try {
      const t = await token();
      const res = await fetch(`${APIURL}/whatsapp/schedules/${id}/send-now`, {
        method: 'POST', headers: { Authorization: `Bearer ${t}` },
      });
      const body = await res.json();
      if (!res.ok || !body.sent) throw new Error(body.reason || body.error || 'Send failed');
      await fetchSchedules();
      alert(`Sent to ${body.sentCount} group(s) ✓`);
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }

  function whenLabel(s: Schedule): string {
    const time = formatTime12hLocal(s.time_of_day);
    if (s.frequency === 'daily') return `Daily ${time}`;
    const days = (s.days_of_week || []).map((d) => d.slice(0, 3).replace(/^./, (c) => c.toUpperCase())).join('/');
    return `${days || 'Thu'} ${time}`;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '950px', margin: '0 auto' }}>
      <h2>WhatsApp Reservations Bot</h2>
      <p style={{ color: '#555', fontSize: 14 }}>
        Pair a WhatsApp account once (this becomes the <strong>BGA Reservations</strong> bot),
        invite it into your group chats, then create schedules that post upcoming tee-time
        announcements automatically.
      </p>

      {/* ── Connection ────────────────────────────────────────────────────── */}
      <section style={card}>
        <h3 style={cardHeading}>Connection</h3>
        {!status ? (
          <p>Loading...</p>
        ) : status.status === 'connected' ? (
          <>
            <p>
              <span style={{ ...badge, background: '#d5f5e3', color: '#1e8449' }}>Connected</span>{' '}
              as <strong>{status.me?.name || status.me?.id}</strong>
              {status.connectedAt && (
                <span style={{ color: '#888', marginLeft: 8 }}>
                  since {new Date(status.connectedAt).toLocaleString()}
                </span>
              )}
            </p>
            <button onClick={disconnect} disabled={busy} style={dangerBtn}>Disconnect / Log out</button>
          </>
        ) : status.status === 'qr' && status.qrDataUrl ? (
          <>
            <p>Scan this QR with the WhatsApp account you want to use as <strong>BGA Reservations</strong>:</p>
            <p style={{ color: '#666', fontSize: 13 }}>
              In WhatsApp → Settings → Linked Devices → Link a Device
            </p>
            <img src={status.qrDataUrl} alt="WhatsApp pairing QR" style={{ width: 280, height: 280, border: '1px solid #ddd', borderRadius: 4 }} />
          </>
        ) : status.status === 'connecting' ? (
          <p><span style={{ ...badge, background: '#fef9e7', color: '#b7950b' }}>Connecting…</span></p>
        ) : (
          <>
            <p>
              <span style={{ ...badge, background: '#f5e8e6', color: '#943126' }}>Disconnected</span>
              {status.lastError && <span style={{ color: '#943126', marginLeft: 8 }}>— {status.lastError}</span>}
            </p>
            <button onClick={connect} disabled={busy} style={primaryBtn}>
              {busy ? 'Starting…' : 'Connect & generate QR'}
            </button>
          </>
        )}
      </section>

      {/* ── Schedules ─────────────────────────────────────────────────────── */}
      <section style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={cardHeading}>Schedules</h3>
          <button onClick={startNew} style={primaryBtn}>+ New schedule</button>
        </div>

        {error && <p style={{ color: '#c0392b', fontSize: 13 }}>{error}</p>}

        {schedules.length === 0 ? (
          <p style={{ color: '#888', fontSize: 14 }}>No schedules yet.</p>
        ) : (
          <FitTable>
          <table className="tbl tbl--plain">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>When</th>
                <th>Groups</th>
                <th>Range</th>
                <th>Last sent</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map((s) => (
                <tr key={s.schedule_id}>
                  <td>{s.name}</td>
                  <td>
                    <span style={{
                      ...badge,
                      background: s.status === 'active' ? '#d5f5e3' : '#f8f0e3',
                      color: s.status === 'active' ? '#1e8449' : '#7d6608',
                    }}>{s.status}</span>
                  </td>
                  <td>{whenLabel(s)}<br /><small style={{ color: '#888' }}>{s.timezone}</small></td>
                  <td>
                    {(s.target_chat_names || []).length > 0
                      ? (s.target_chat_names || []).join(', ')
                      : <span style={{ color: '#aaa' }}>—</span>}
                  </td>
                  <td>+{s.date_range_start_offset}d, {s.date_range_days}d span</td>
                  <td>{s.last_sent_at ? new Date(s.last_sent_at).toLocaleString() : <span style={{ color: '#aaa' }}>never</span>}</td>
                  <td className="nowrap">
                    <button onClick={() => previewSaved(s.schedule_id)} style={smallBtn}>Preview</button>{' '}
                    <button onClick={() => sendNow(s.schedule_id)} style={{ ...smallBtn, background: '#27ae60' }}>Send now</button>{' '}
                    <button onClick={() => startEdit(s)} style={smallBtn}>Edit</button>{' '}
                    <button onClick={() => deleteSchedule(s.schedule_id)} style={{ ...smallBtn, background: '#e74c3c' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </FitTable>
        )}

        {preview && (
          <div style={{ marginTop: 16, padding: 12, background: '#f4faf7', border: '1px solid #c8e6c9', borderRadius: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <strong>{preview.scope}</strong>
              <button onClick={() => setPreview(null)} style={{ ...smallBtn, background: '#888' }}>Close</button>
            </div>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 14, margin: 0 }}>{preview.text}</pre>
          </div>
        )}
      </section>

      {/* ── Editor ────────────────────────────────────────────────────────── */}
      {editing && (
        <section style={card}>
          <h3 style={cardHeading}>{editing.schedule_id ? 'Edit schedule' : 'New schedule'}</h3>
          <ScheduleForm
            draft={editing}
            setDraft={setEditing}
            groups={groups}
            onRefreshGroups={() => fetchGroups(true)}
            connected={status?.status === 'connected'}
          />
          <div style={{ marginTop: 14, display: 'flex', gap: 10 }}>
            <button onClick={saveSchedule} disabled={busy} style={primaryBtn}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button onClick={previewDraft} disabled={busy} style={{ ...smallBtn, background: '#2980b9', padding: '7px 14px', fontSize: 14 }}>
              Preview draft
            </button>
            <button onClick={cancelEdit} style={{ ...smallBtn, background: '#888', padding: '7px 14px', fontSize: 14 }}>Cancel</button>
          </div>
          {error && <p style={{ color: '#c0392b', fontSize: 13 }}>{error}</p>}
        </section>
      )}
    </div>
  );
}

// ── Editor ──────────────────────────────────────────────────────────────────

interface ScheduleFormProps {
  draft: Partial<Schedule>;
  setDraft: (s: Partial<Schedule>) => void;
  groups: WhatsAppGroup[];
  onRefreshGroups: () => void;
  connected: boolean;
}

function ScheduleForm({ draft, setDraft, groups, onRefreshGroups, connected }: ScheduleFormProps) {
  function set<K extends keyof Schedule>(k: K, v: Schedule[K]) {
    setDraft({ ...draft, [k]: v });
  }

  function toggleGroup(id: string) {
    const cur = new Set(draft.target_chat_ids || []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    set('target_chat_ids', Array.from(cur) as any);
  }

  function toggleDay(day: string) {
    const cur = new Set(draft.days_of_week || []);
    if (cur.has(day)) cur.delete(day); else cur.add(day);
    set('days_of_week', Array.from(cur) as any);
  }

  return (
    <>
      {/* Status row — pinned to the top */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: 10, background: '#f9f9f9', borderRadius: 6 }}>
        <label style={{ fontWeight: 600, fontSize: 13 }}>Status</label>
        <select
          value={draft.status || 'active'}
          onChange={(e) => set('status', e.target.value as Schedule['status'])}
          style={{ ...inp, minWidth: 120 }}
        >
          <option value="active">Active</option>
          <option value="paused">Paused</option>
        </select>
        <span style={{ color: '#888', fontSize: 12 }}>
          {draft.status === 'paused' ? 'This schedule will not fire until set to Active.' : 'This schedule will fire on the configured frequency.'}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Name">
          <input style={inp} value={draft.name || ''} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Timezone">
          <input style={inp} value={draft.timezone || ''} onChange={(e) => set('timezone', e.target.value)} placeholder="America/Toronto" />
        </Field>

        {/* Simple scheduler */}
        <Field label="Frequency">
          <select style={inp} value={draft.frequency || 'weekly'} onChange={(e) => set('frequency', e.target.value as any)}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
        </Field>
        <Field label="Time of day">
          <input
            type="time"
            style={inp}
            value={draft.time_of_day || '09:00'}
            onChange={(e) => set('time_of_day', e.target.value)}
          />
        </Field>

        {draft.frequency === 'weekly' && (
          <Field label="Days of week" hint="Pick one or more">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {DAYS.map((d) => {
                const on = (draft.days_of_week || []).includes(d.value);
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleDay(d.value)}
                    style={{
                      ...smallBtn,
                      background: on ? '#2980b9' : '#e0e0e0',
                      color: on ? '#fff' : '#444',
                      padding: '6px 10px',
                      fontSize: 12,
                      minWidth: 40,
                    }}
                  >{d.label}</button>
                );
              })}
            </div>
          </Field>
        )}

        {/* Target groups (multi) */}
        <Field label="Target groups" hint="Bot must already be invited">
          {connected ? (
            <>
              <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 4, padding: 6 }}>
                {groups.length === 0 ? (
                  <p style={{ color: '#888', fontSize: 13, margin: 4 }}>No groups found. Invite the bot into a group on your phone, then refresh.</p>
                ) : groups.map((g) => {
                  const on = (draft.target_chat_ids || []).includes(g.id);
                  return (
                    <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 4px', fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={on} onChange={() => toggleGroup(g.id)} />
                      <span>{g.name}</span>
                      <span style={{ color: '#888', fontSize: 11 }}>({g.participants})</span>
                    </label>
                  );
                })}
              </div>
              <button type="button" onClick={onRefreshGroups} style={{ ...smallBtn, marginTop: 4 }}>Refresh group list</button>
            </>
          ) : (
            <p style={{ color: '#888', fontSize: 13 }}>Connect WhatsApp first to pick groups.</p>
          )}
        </Field>

        <Field label="Date range" hint="Reservations within this window are included">
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 13 }}>Start in</span>
            <input type="number" style={{ ...inp, width: 60 }} value={draft.date_range_start_offset ?? 0}
              onChange={(e) => set('date_range_start_offset', Number(e.target.value))} />
            <span style={{ fontSize: 13 }}>day(s), span</span>
            <input type="number" style={{ ...inp, width: 60 }} value={draft.date_range_days ?? 7}
              onChange={(e) => set('date_range_days', Number(e.target.value))} />
            <span style={{ fontSize: 13 }}>day(s)</span>
          </div>
        </Field>

        <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Field label="Message prefix" hint="Optional intro text rendered above the header. Supports WhatsApp markdown.">
            <textarea
              style={{ ...inp, fontFamily: 'inherit', resize: 'vertical', minHeight: 50 }}
              value={draft.message_prefix || ''}
              onChange={(e) => set('message_prefix', e.target.value as any)}
              placeholder="e.g. Good morning Broskies! Here are this week's openings."
            />
          </Field>
          <Field label="Header text" hint="Bolded automatically in WhatsApp. Leave blank to skip.">
            <input style={inp} value={draft.header_text || ''} onChange={(e) => set('header_text', e.target.value)} />
          </Field>
          <Field label="Message suffix" hint="Optional sign-off rendered below the last day. Supports WhatsApp markdown.">
            <textarea
              style={{ ...inp, fontFamily: 'inherit', resize: 'vertical', minHeight: 50 }}
              value={draft.message_suffix || ''}
              onChange={(e) => set('message_suffix', e.target.value as any)}
              placeholder="e.g. Reply with the tee time you want — first come first served!"
            />
          </Field>
        </div>

        <Field label="Reservation statuses">
          <div style={{ display: 'flex', gap: 12, fontSize: 13 }}>
            {(['active', 'cancelled'] as const).map((st) => (
              <label key={st} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="checkbox"
                  checked={(draft.reservation_statuses || []).includes(st)}
                  onChange={(e) => {
                    const next = new Set(draft.reservation_statuses || []);
                    if (e.target.checked) next.add(st); else next.delete(st);
                    set('reservation_statuses', Array.from(next) as any);
                  }}
                />
                {st}
              </label>
            ))}
          </div>
        </Field>

        <Field label="Filters & layout">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={!!draft.only_with_open_seats}
                onChange={(e) => set('only_with_open_seats', e.target.checked as any)}
              />
              Only include reservations with open seats
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={!!draft.blank_line_between_tee_times}
                onChange={(e) => set('blank_line_between_tee_times', e.target.checked as any)}
              />
              Add a blank line between tee times
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={!!draft.strikethrough_cancelled}
                onChange={(e) => set('strikethrough_cancelled', e.target.checked as any)}
              />
              <span>
                <s>Strikethrough</s> cancelled tee times
                <small style={{ color: '#888', marginLeft: 6 }}>(requires "cancelled" in reservation statuses)</small>
              </span>
            </label>
          </div>
        </Field>
      </div>

      {/* Per-line template editor */}
      <div style={{ marginTop: 18 }}>
        <Field
          label="Line format"
          hint="Each tee-time row is built from this template. A token's prefix vanishes with the token when the value is empty."
        >
          <TemplateEditor
            template={draft.line_template || []}
            setTemplate={(t) => set('line_template', t as any)}
          />
        </Field>
      </div>
    </>
  );
}

// ── Template editor ─────────────────────────────────────────────────────────

interface TemplateEditorProps {
  template: Segment[];
  setTemplate: (t: Segment[]) => void;
}

function TemplateEditor({ template, setTemplate }: TemplateEditorProps) {
  const move = (i: number, delta: number) => {
    const j = i + delta;
    if (j < 0 || j >= template.length) return;
    const next = [...template];
    [next[i], next[j]] = [next[j], next[i]];
    setTemplate(next);
  };
  const remove = (i: number) => setTemplate(template.filter((_, idx) => idx !== i));
  const update = (i: number, seg: Segment) => setTemplate(template.map((s, idx) => idx === i ? seg : s));
  const addToken = (name: TokenName) => setTemplate([...template, { type: 'token', name, prefix: ' ' }]);
  const addText = () => setTemplate([...template, { type: 'text', value: '' }]);
  const reset = () => setTemplate([...DEFAULT_TEMPLATE]);

  const livePreview = useMemo(() => previewWithSample(template), [template]);

  return (
    <div>
      {/* Token palette */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        <span style={{ alignSelf: 'center', fontSize: 12, color: '#666', marginRight: 4 }}>Add token:</span>
        {(Object.entries(TOKEN_META) as [TokenName, typeof TOKEN_META[TokenName]][]).map(([name, meta]) => (
          <button
            key={name}
            type="button"
            onClick={() => addToken(name)}
            title={meta.sample}
            style={tokenChipBtn}
          >+ {meta.label}</button>
        ))}
        <button type="button" onClick={addText} style={{ ...tokenChipBtn, background: '#fff8e1', borderColor: '#f1c40f' }}>+ Free text</button>
        <button type="button" onClick={reset} style={{ ...tokenChipBtn, background: '#fce8e8', borderColor: '#e74c3c' }}>Reset to default</button>
      </div>

      {/* Segments list */}
      {template.length === 0 ? (
        <p style={{ color: '#888', fontSize: 13 }}>No segments. Click a token above to start.</p>
      ) : (
        <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {template.map((seg, i) => (
            <li key={i} style={segmentRow}>
              <span style={{ width: 18, color: '#aaa', fontSize: 11 }}>{i + 1}.</span>
              {seg.type === 'token' ? (
                <>
                  <span style={tokenChip}>{TOKEN_META[seg.name]?.label || seg.name}</span>
                  <span style={{ fontSize: 12, color: '#666' }}>prefix:</span>
                  <input
                    style={{ ...inp, padding: '4px 8px', fontSize: 13, width: 170 }}
                    placeholder="before (hidden if empty)"
                    value={seg.prefix || ''}
                    onChange={(e) => update(i, { ...seg, prefix: e.target.value })}
                  />
                  <span style={{ fontSize: 12, color: '#666' }}>suffix:</span>
                  <input
                    style={{ ...inp, padding: '4px 8px', fontSize: 13, width: 170 }}
                    placeholder="after (hidden if empty)"
                    value={seg.suffix || ''}
                    onChange={(e) => update(i, { ...seg, suffix: e.target.value })}
                  />
                </>
              ) : (
                <>
                  <span style={{ ...tokenChip, background: '#fff8e1', color: '#7d6608' }}>Text</span>
                  <input
                    style={{ ...inp, padding: '4px 8px', fontSize: 13, flex: 1 }}
                    placeholder="(always rendered as-is)"
                    value={seg.value}
                    onChange={(e) => update(i, { ...seg, value: e.target.value })}
                  />
                </>
              )}
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} style={iconBtn}>↑</button>
                <button type="button" onClick={() => move(i, +1)} disabled={i === template.length - 1} style={iconBtn}>↓</button>
                <button type="button" onClick={() => remove(i)} style={{ ...iconBtn, background: '#e74c3c', color: '#fff' }}>✕</button>
              </span>
            </li>
          ))}
        </ol>
      )}

      {/* Live preview with sample data */}
      <div style={{ marginTop: 10, padding: 10, background: '#f9fbfc', border: '1px dashed #ccd', borderRadius: 4, fontSize: 13 }}>
        <strong style={{ color: '#666', fontSize: 12 }}>Live preview (sample data):</strong>
        <div style={{ marginTop: 4, fontFamily: 'monospace' }}>{livePreview || <span style={{ color: '#aaa' }}>(empty)</span>}</div>
      </div>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const SAMPLE = {
  complex_name: 'Glen Abbey',
  course_name: 'Island Course',
  tee_time: '07:44:00',
  num_players: 4,
  joined_player_count: 2,
  reservation_name: 'Smith',
  booked_by_alias: 'twong60@bgagolf.ca',
  joined_players: [
    { first_name: 'Terry', last_name: 'Wong' },
    { first_name: 'Mike', last_name: 'Lee' },
  ],
};

function formatTime12hLocal(hhmm: string): string {
  if (!hhmm) return '';
  const m = hhmm.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return hhmm;
  let h = parseInt(m[1], 10);
  const period = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12; else if (h > 12) h -= 12;
  return `${h}:${m[2]} ${period}`;
}

function buildVenueLocal(facility: string, course: string): string {
  const f = (facility || '').trim();
  const c = (course || '').trim();
  if (!f && !c) return '';
  if (!f) return c;
  if (!c) return f;
  const fl = f.toLowerCase(), cl = c.toLowerCase();
  if (fl === cl || fl.includes(cl)) return f;
  if (cl.includes(fl)) return c;
  return `${f} - ${c}`;
}

function resolveTokenLocal(name: TokenName): string {
  const r = SAMPLE as any;
  switch (name) {
    case 'venue':            return buildVenueLocal(r.complex_name, r.course_name);
    case 'facility':         return r.complex_name || '';
    case 'course':           return r.course_name || '';
    case 'time':             return formatTime12hLocal(r.tee_time);
    case 'num_players':      return String(r.num_players ?? '');
    case 'joined_count':     return String(r.joined_player_count ?? '');
    case 'open_seats':       return String(Math.max(0, (r.num_players || 0) - (r.joined_player_count || 0)));
    case 'reservation_name': return r.reservation_name || '';
    case 'email_alias':      return r.booked_by_alias || '';
    case 'joined_players':   return (r.joined_players || []).map((p: any) => [p.first_name, p.last_name].filter(Boolean).join(' ')).join(', ');
    default:                 return '';
  }
}

function previewWithSample(template: Segment[]): string {
  let out = '';
  for (const seg of template) {
    if (seg.type === 'text') out += seg.value || '';
    else if (seg.type === 'token') {
      const v = resolveTokenLocal(seg.name);
      if (v) out += (seg.prefix || '') + v + (seg.suffix || '');
    }
  }
  return out.replace(/\s+/g, ' ').trim();
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#444' }}>{label}</label>
      {children}
      {hint && <small style={{ color: '#888' }}>{hint}</small>}
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const card: React.CSSProperties = {
  background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8,
  padding: 20, marginBottom: 20,
};
const cardHeading: React.CSSProperties = { margin: '0 0 14px 0', fontSize: 16 };
const badge: React.CSSProperties = {
  borderRadius: 4, padding: '0.15em 0.6em', fontSize: '0.86em', fontWeight: 600,
};
const inp: React.CSSProperties = {
  padding: '6px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14,
};
const primaryBtn: React.CSSProperties = {
  padding: '7px 14px', background: '#2980b9', color: '#fff',
  border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 14,
};
const dangerBtn: React.CSSProperties = {
  padding: '6px 12px', background: '#e74c3c', color: '#fff',
  border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13,
};
const smallBtn: React.CSSProperties = {
  padding: '0.25em 0.6em', background: '#555', color: '#fff',
  border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: '0.86em',
};
const tokenChipBtn: React.CSSProperties = {
  padding: '4px 8px', background: '#e8f4fd', border: '1px solid #aed6f1',
  color: '#1f6391', borderRadius: 12, cursor: 'pointer', fontSize: 12,
};
const tokenChip: React.CSSProperties = {
  padding: '3px 8px', background: '#e8f4fd', color: '#1f6391',
  borderRadius: 12, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
};
const segmentRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '6px 8px', background: '#fafafa',
  border: '1px solid #eee', borderRadius: 4,
};
const iconBtn: React.CSSProperties = {
  padding: '2px 6px', background: '#e0e0e0', color: '#444',
  border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: 12, minWidth: 24,
};
